import React from 'react';
import * as Sentry from '@sentry/react';
import { Button, Flex, Typography, theme } from '@robinpowered/ui-kit';
import { useIframeResize } from '@/hooks';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import type { RobinThemeToken } from '@/src/theme/robinTheme';

type Props = {
  children: React.ReactNode;
};

type State = {
  hasError: boolean;
};

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    Sentry.captureException(error, {
      extra: { componentStack: errorInfo.componentStack },
    });
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback />;
    }
    return this.props.children;
  }
}

const ErrorFallback = () => {
  useIframeResize();
  const { token } = theme.useToken();
  const { t } = useTranslation('ErrorBoundary');

  return (
    <Container align="center" justify="center" vertical gap={Number(token.margin)} $token={token}>
      <ErrorText>{t('message')}</ErrorText>
      <Button onClick={() => window.location.reload()}>{t('retry')}</Button>
    </Container>
  );
};

const Container = styled(Flex)<{ $token: RobinThemeToken }>`
  width: 100%;
  max-width: 420px;
  background: ${(p) => String(p.$token.colorBgLayout)};
  border: 1px solid ${(p) => String(p.$token.colorBorderSecondary)};
  box-shadow: ${(p) => String(p.$token.boxShadowTertiary)};
  border-radius: ${(p) => Number(p.$token.borderRadiusLG)}px;
  box-sizing: border-box;
  overflow: hidden;
  padding: ${(p) => Number(p.$token.padding)}px;
`;

const ErrorText = styled(Typography.Text)`
  text-align: center;
`;
